import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { LanguagePickerV2 } from '@shared/components/LanguagePickerV2';
import { CURRENCY_BUTTON_PROPS } from '@shared/components/CurrencySelectorV2';
import { GuestSiteTypographyOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride';
import { button2ToButtonsOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride.constants';
import type { ComboboxButtonProps } from '@withjoy/joykit/components/Combobox/Combobox.types';
import { useTranslation } from '@shared/core';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { useEventInfo } from '@shared/utils/eventInfo';
import i18n from '@shared/core/i18n/i18n';
import { resolveSupportedLocale } from '@shared/core/i18n/utils';
import { LANGUAGES } from '@shared/core/i18n/i18n.constants';
import { getEnabledGuestLanguages, writeGuestLanguageCookie } from '@shared/core/i18n/guestLanguage';
import { updateLocaleSearchParams } from '@shared/hooks/useGeoLocale';
import { EventType } from '@graphql/generated-base';
import type { GuestLanguageSelectorSurface } from './GuestLanguageSelector.types';
import { TelemetryProvider, useGuestLanguageSelectorTelemetry } from './GuestLanguageSelector.telemetry';
import { useColorPaletteProvider } from '@apps/guest/packages/layout-engine/layouts/ColorPaletteProvider/ColorPaletteProvider';
import { hasReadableContrast, shiftToneForSubtleContrast } from '@shared/utils/color';

type GuestLanguageSelectorProps = Readonly<{
  /** Which chrome hosts this instance — distinguishes interaction telemetry per mount. */
  surface: GuestLanguageSelectorSurface;
}>;

// In the stays top bar the language button sits beside the currency selector, so it
// borrows the currency pill styling (ghost, mono2, 32px, rounded) — width auto since
// it's icon-only.
const STAYS_TOP_BAR_BUTTON_PROPS: ComboboxButtonProps = {
  ...CURRENCY_BUTTON_PROPS,
  overrides: {
    ...CURRENCY_BUTTON_PROPS.overrides,
    Root: {
      ...CURRENCY_BUTTON_PROPS.overrides?.Root,
      props: {
        ...CURRENCY_BUTTON_PROPS.overrides?.Root?.props,
        width: 'auto',
        paddingX: 3,
      },
    },
  },
};

// Guest-site navigation styling: a solid, event-themed pill. Colors and fonts come from
// the guest-site theme via GuestSiteTypographyOverride below (the same buttons override
// the nav/drawer controls use), so every navigation placement looks identical and only
// its position on the page differs.
const DEFAULT_BUTTON_PROPS: ComboboxButtonProps = { variant: 'solid', intent: 'primary', shape: 'rounded' };

/** Below this many choices the dialog shows no search field. Exported for the spec. */
export const SEARCH_MIN_LANGUAGE_COUNT = 10;

/**
 * Trigger styling matching the floating menu (burger) button — same pageColor fill,
 * pageFontColor glyph and hover shift, so the two controls read as one set of chrome.
 * `_active` has to be set too: the solid/primary variant otherwise supplies the brand
 * accent on press, which the burger (a plain Box) never shows.
 *
 * Falls back to the designed solid pair when the couple's palette is missing or the
 * fill/glyph combination wouldn't be legible — the glyph is the only content of an
 * icon-only trigger, so a low-contrast pair makes the control read as empty.
 */
const getMenuChromeButtonProps = (pageColor?: string, pageFontColor?: string): ComboboxButtonProps => {
  if (!pageColor || !pageFontColor || !hasReadableContrast(pageColor, pageFontColor)) {
    return DEFAULT_BUTTON_PROPS;
  }
  const pressedBackground = shiftToneForSubtleContrast(pageColor, { lightenBy: 0.1, darkenBy: 0.1 });
  return {
    ...DEFAULT_BUTTON_PROPS,
    overrides: {
      Root: {
        props: {
          backgroundColor: pageColor,
          color: pageFontColor,
          _hover: { backgroundColor: pressedBackground },
          _active: { backgroundColor: pressedBackground },
        },
      },
    },
  };
};

/**
 * Guest-facing language picker (PRNT-4574). Renders only when the couple
 * enabled more than one guest display language and the selector flag is on.
 * A pick persists per event (path-scoped cookie, read by SSR) and swaps the
 * i18n language immediately; couple content is never translated.
 */
/**
 * Whether the selector would render — for mount points that need to skip their
 * own wrapper markup (e.g. the Brannan nav's list item) when it is hidden.
 */
export const useGuestLanguageSelectorAvailability = (): boolean => {
  const { eventInfo, ssrEventInfo } = useEventInfo();
  // Wedding-only product decision (PRNT-4604): Joy Pro / baby registry / card events never
  // show the selector — skip the flag reads so Amplitude doesn't track exposure for them.
  // ssrEventInfo covers the SSR/initial-load window where eventInfo is not yet populated.
  const isWeddingEvent = (eventInfo?.eventType ?? ssrEventInfo?.eventType) === EventType.wedding;
  const [isSelectorEnabled] = useIsFeatureEnabled('guestSiteLanguageSelectorEnabled', { skip: !isWeddingEvent });
  const [areTranslationsEnabled] = useIsFeatureEnabled('joyWebTranslationsEnabled', { skip: !isWeddingEvent });
  const enabledLanguages = useMemo(() => getEnabledGuestLanguages(eventInfo?.locale, eventInfo?.guestDisplayLocales), [eventInfo?.locale, eventInfo?.guestDisplayLocales]);
  return Boolean(isSelectorEnabled && areTranslationsEnabled && isWeddingEvent && enabledLanguages.length >= 2);
};

const GuestLanguageSelectorContent: React.FC<GuestLanguageSelectorProps> = ({ surface }) => {
  const { tq } = useTranslation('languagePickerV2');
  const isAvailable = useGuestLanguageSelectorAvailability();
  const { eventInfo, eventHandle } = useEventInfo();
  const telemetry = useGuestLanguageSelectorTelemetry();
  // Non-strict: the stays top bar renders outside ColorPaletteProvider, and the repo already
  // reads this context that way (see WeatherReport.colors). Keeps the hook order unconditional.
  const { pageColor, pageFontColor } = useColorPaletteProvider(false) || {};

  const [language, setLanguage] = useState(() => resolveSupportedLocale(i18n.language || LANGUAGES.ENGLISH_BASE).locale);
  useEffect(() => {
    const handleLanguageChanged = (nextLanguage: string) => setLanguage(resolveSupportedLocale(nextLanguage).locale);
    i18n.on('languageChanged', handleLanguageChanged);
    return () => i18n.off('languageChanged', handleLanguageChanged);
  }, []);

  const enabledLanguages = useMemo(() => getEnabledGuestLanguages(eventInfo?.locale, eventInfo?.guestDisplayLocales), [eventInfo?.locale, eventInfo?.guestDisplayLocales]);

  const handleChange = useCallback(
    (languageCode: string) => {
      // Single-select Combobox fires onValueChange even for the already-selected
      // option — not a language change, and it would inflate the adoption metric.
      if (languageCode === language) {
        return;
      }
      telemetry.languageSelected(surface, language, languageCode);
      if (eventHandle) {
        writeGuestLanguageCookie(languageCode, eventHandle);
      }
      // If the URL already carries a language param it would outrank the cookie — keep it in step.
      updateLocaleSearchParams({ language: languageCode });
      i18n.changeLanguage(languageCode);
    },
    [eventHandle, language, surface, telemetry],
  );

  const handleOpenChange = useCallback(
    (isOpen: boolean) => {
      if (isOpen) {
        telemetry.languageSelectorOpened(surface);
      }
    },
    [surface, telemetry],
  );

  if (!isAvailable) {
    return null;
  }

  // A search field is noise for a handful of languages; it earns its place only once the
  // list is long enough to scan poorly.
  const searchable = enabledLanguages.length >= SEARCH_MIN_LANGUAGE_COUNT;

  const picker = (buttonProps: ComboboxButtonProps) => (
    <LanguagePickerV2
      mode="button"
      triggerVariant="icon"
      optionLayout="stacked"
      searchable={searchable}
      buttonProps={buttonProps}
      aria-label={tq`Choose language`}
      value={language}
      onChange={handleChange}
      onOpenChange={handleOpenChange}
      allowedLanguageCodes={enabledLanguages}
    />
  );

  // The stays top bar matches the currency selector beside it, and renders outside
  // EventDesignThemingProvider (a strict context), so it skips the typography override.
  if (surface === 'staysTopBar') {
    return picker(STAYS_TOP_BAR_BUTTON_PROPS);
  }

  return <GuestSiteTypographyOverride override={button2ToButtonsOverride}>{picker(getMenuChromeButtonProps(pageColor, pageFontColor))}</GuestSiteTypographyOverride>;
};

export const GuestLanguageSelector: React.FC<GuestLanguageSelectorProps> = props => (
  <TelemetryProvider>
    <GuestLanguageSelectorContent {...props} />
  </TelemetryProvider>
);

GuestLanguageSelector.displayName = 'GuestLanguageSelector';
